// @ts-strict-ignore
import { ForwardedRef, forwardRef, memo, useMemo } from 'react'
import { flatten, uniqBy } from 'lodash'
import { getCountryGroupNames, getCountryGroup, getCountryGroupOptions } from 'services/location'
import { FormValueInput } from 'types'
import { useCountriesAutocomplete } from 'hooks/location'
import { IAutoCompleteOption } from 'components/AutoCompleteTextField'
import { trackError } from 'services/analytics'
import AutoCompleteChipInput from './AutoCompleteChipInput'
import { IAutoCompleteChipInputProps } from './AutoCompleteChipInput/AutoCompleteChipInput'

interface CountryItem {
  code: string
  name: string
}

function cleanValue(value: CountryItem[]): IAutoCompleteOption[] {
  return (value || [])
    .filter(v => Boolean(v))
    .map(v => ({ label: v.name, value: v.code }))
}

async function prepareValuesForChangeEvent(values: IAutoCompleteOption[]): Promise<CountryItem[]> {
  const groupNames = getCountryGroupNames()
  const results = values.filter(v => Boolean(v && v.value)).map(v => (
    // We need to identify if the option is a country group. If so, we call a function to turn the country group name
    // into the actual country options for the countries in the group
    // NOTE: this means that we have to flatten a array, and resolve a promise, because the function that gets the
    // countries in the group is async due to an API call being needed.
    (groupNames.indexOf(v.value as string) >= 0 ? getCountryGroupOptions(v.value as string) : { code: v.value as string, name: v.label })
  ))
  // Now we resolve any promises in parallel, then we flatten and uniq the results by code.
  return uniqBy(flatten(await Promise.all(results)), c => c.code)
}

export interface ICountrySelectorProps extends Omit<IAutoCompleteChipInputProps<IAutoCompleteOption>, 'input' | 'value' | 'onChange'> {
  label?: string
  input: FormValueInput<CountryItem[]>
}

const CountrySelector = memo(forwardRef((props: ICountrySelectorProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    label = 'Countries',
    input: { value, onChange, name, ...inputProps },
    ...otherProps
  } = props

  const cleanedValue = useMemo(() => cleanValue(value), [value])
  const autocomplete = useCountriesAutocomplete()
  const suggestions = useMemo(() => {
    const countryGroupSuggestions = getCountryGroupNames().map(groupName => ({
      value: groupName, label: `${groupName} (${getCountryGroup(groupName).length} countries)`,
    }))
    return [...countryGroupSuggestions, ...autocomplete.suggestions]
  }, [autocomplete])

  const { filterOptions: _filterOptions, ...autocompleteProps } = autocomplete

  return (
    <AutoCompleteChipInput
      {...autocompleteProps}
      suggestions={suggestions}
      input={{
        value: cleanedValue,
        onChange: (values) => {
          prepareValuesForChangeEvent(values)
            .then(onChange)
            .catch((err) => { trackError(err, 'high') })
        },
        name: name || 'countries',
        ...inputProps,
      }}
      label={label}
      {...otherProps}
      ref={ref}
    />
  )
}))

export default memo(CountrySelector)
